import React, { createContext, useState, useContext, ReactNode } from 'react';
import { Deal } from '@components/dashboard/data/DealsAndResources';
import { BookingPerson, SpecialistSection } from '@components/dashboard/data/Specialists';
import { TransformedResource } from '@contracts';

interface ModalState<T> {
  isOpen: boolean;
  data?: T;
}

interface ModalContextProps<T> {
  modal: ModalState<T>;
  openModal: (data?: T) => void;
  closeModal: () => void;
}

interface ModalProviderProps<T> {
  children: ReactNode;
  initialState?: ModalState<T>;
}

const createModalProvider = <T,>(defaultValue: T) => {
  const ModalContext = createContext<ModalContextProps<T> | null>(null);

  const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
      throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
  };

  const ModalProvider: React.FC<ModalProviderProps<T>> = ({ children, initialState }) => {
    const initialModalState = initialState || { isOpen: false };
    const [modal, setModal] = useState<ModalState<T>>(initialModalState);

    const openModal = (data?: T) => {
      setModal({ isOpen: true, data });
    };

    const closeModal = () => {
      setModal({ isOpen: false, data: defaultValue });
    };

    return <ModalContext.Provider value={{ modal, openModal, closeModal }}>{children}</ModalContext.Provider>;
  };

  return { ModalProvider, useModal };
};

export const UnlockModalContext = createModalProvider<null>(null);
export const DealModalContext = createModalProvider<Deal | TransformedResource | null>(null);
export const SpecialistModalContext = createModalProvider<{ section: SpecialistSection | null; person: BookingPerson | null }>({
  section: null,
  person: null
});
